package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.util.addPostToServerQueue
import pl.zar.tikthrow.util.createPostsSettings
import pl.zar.tikthrow.util.loadPlanFromServer
import pl.zar.tikthrow.util.loadServerQueue
import pl.zar.tikthrow.util.removePostFromServerQueue
import pl.zar.tikthrow.util.reorderServerQueue
import pl.zar.tikthrow.util.retryPostOnServer
import pl.zar.tikthrow.util.savePlanToServer
import pl.zar.tikthrow.util.updateQueuedPostOnServer

@Serializable
data class QueuedPost(
    val id: String,
    val type: ContentType,
    val media: List<MediaItem>,
    val title: String = "",
    val caption: String,
    val platforms: Set<Platform>,
    val youtubeAccountId: String? = null,
    val tiktokAccountId: String? = null,
    val addedAtMillis: Long,
    val status: String = "PENDING",
    val errorMessage: String? = null,
)

@Serializable
enum class PlanMode { SLOTS, FREQUENCY }

@Serializable
data class WeeklySlot(
    val dayOfWeek: Int,
    val hour: Int,
    val minute: Int,
)

@Serializable
data class PublishingPlan(
    val mode: PlanMode = PlanMode.SLOTS,
    val weeklySlots: List<WeeklySlot> = emptyList(),
    val postsPerWeek: Int = 3,
    val preferredHour: Int = 12,
    val preferredMinute: Int = 0,
    val isActive: Boolean = false,
    val platforms: Set<Platform> = emptySet(),
    val accountIds: Set<String> = emptySet(),
    /** IANA zone the slots are expressed in; the server resolves them in this zone. */
    val timezone: String = "UTC",
    /** When set, the server publishes one queued post every N minutes instead of using the slots. */
    val testIntervalMinutes: Int? = null,
    /** Epoch millis of the last slot/interval the server actually consumed. Server-computed, read-only. */
    val lastPublishedAtMillis: Long? = null,
)

class QueueViewModel : ViewModel() {
    private val settings = runCatching { createPostsSettings() }.getOrNull()
    private val json = Json { ignoreUnknownKeys = true }

    private val _queue = MutableStateFlow<List<QueuedPost>>(loadQueue())
    val queue: StateFlow<List<QueuedPost>> = _queue.asStateFlow()

    private val _plan = MutableStateFlow(loadPlan())
    val plan: StateFlow<PublishingPlan> = _plan.asStateFlow()

    private val _publishFeedback = MutableStateFlow<String?>(null)
    val publishFeedback: StateFlow<String?> = _publishFeedback.asStateFlow()

    init {
        viewModelScope.launch { syncWithServer() }
        viewModelScope.launch { pollWhileTestModeActive() }
    }

    /**
     * Test mode publishes every couple of minutes, so the queue/plan state (and the countdown
     * built on top of it) would otherwise go stale until the user leaves and re-enters the screen.
     * Cheap no-op while test mode is off — just a local flag check, no network call.
     */
    private suspend fun pollWhileTestModeActive() {
        while (true) {
            delay(5_000L)
            if (_plan.value.testIntervalMinutes != null) {
                syncWithServer()
            }
        }
    }

    /** Disambiguates ids when [addPost] is called several times in the same millisecond (batch add). */
    private var tempIdSuffix = 0

    fun addPost(
        type: ContentType,
        media: List<MediaItem>,
        title: String,
        caption: String,
        platforms: Set<Platform>,
        youtubeAccountId: String? = null,
        tiktokAccountId: String? = null,
    ) {
        val tempId = "${Clock.System.now().toEpochMilliseconds()}-${tempIdSuffix++}"
        val post = QueuedPost(
            id = tempId,
            type = type,
            media = media,
            title = title.trim(),
            caption = caption,
            platforms = platforms,
            youtubeAccountId = youtubeAccountId,
            tiktokAccountId = tiktokAccountId,
            addedAtMillis = Clock.System.now().toEpochMilliseconds(),
        )
        _queue.update { it + post }
        persistQueue()

        viewModelScope.launch {
            val serverId = runCatching { addPostToServerQueue(post, media) }.getOrNull()
            if (serverId != null && serverId != tempId) {
                _queue.update { list -> list.map { if (it.id == tempId) it.copy(id = serverId) else it } }
                persistQueue()
            }
        }
    }

    /** Edit the title/description a queued post will publish with. */
    fun updateContent(id: String, title: String, caption: String) {
        _queue.update { list ->
            list.map { if (it.id == id) it.copy(title = title.trim(), caption = caption) else it }
        }
        persistQueue()
        viewModelScope.launch { runCatching { updateQueuedPostOnServer(id, title.trim(), caption) } }
    }

    fun removePost(id: String) {
        _queue.update { it.filter { p -> p.id != id } }
        persistQueue()
        viewModelScope.launch { runCatching { removePostFromServerQueue(id) } }
    }

    fun retryPost(id: String) {
        _queue.update { list -> list.map { if (it.id == id) it.copy(status = "PENDING", errorMessage = null) else it } }
        persistQueue()
        viewModelScope.launch {
            val error = runCatching { retryPostOnServer(id) }.getOrElse { it.message ?: "Nieznany błąd" }
            if (error != null) {
                // The server rejected the retry (e.g. the media file is gone) — don't leave the
                // post showing PENDING when nothing will actually happen with it.
                _queue.update { list -> list.map { if (it.id == id) it.copy(status = "FAILED", errorMessage = error) else it } }
                persistQueue()
                _publishFeedback.value = error
            }
        }
    }

    fun moveUp(id: String) {
        _queue.update { list ->
            val idx = list.indexOfFirst { it.id == id }
            if (idx <= 0) list
            else list.toMutableList().also { it.add(idx - 1, it.removeAt(idx)) }
        }
        persistQueue()
        syncReorder()
    }

    fun moveDown(id: String) {
        _queue.update { list ->
            val idx = list.indexOfFirst { it.id == id }
            if (idx < 0 || idx >= list.lastIndex) list
            else list.toMutableList().also { it.add(idx + 1, it.removeAt(idx)) }
        }
        persistQueue()
        syncReorder()
    }

    fun savePlan(plan: PublishingPlan) {
        _plan.value = plan.withCurrentTimezone()
        persistPlan()
        viewModelScope.launch { runCatching { savePlanToServer(_plan.value) } }
    }

    fun togglePlanActive() {
        _plan.update { it.copy(isActive = !it.isActive).withCurrentTimezone() }
        persistPlan()
        viewModelScope.launch { runCatching { savePlanToServer(_plan.value) } }
    }

    /**
     * Turn test mode on with the given cadence, or off when [minutes] is null.
     *
     * Turning test mode on also activates the plan — test mode is meaningless while the plan is
     * paused, since the scheduler only ever looks at active plans. Turning test mode off leaves
     * `isActive` alone so we don't pause a real plan as a side effect.
     */
    fun setTestInterval(minutes: Int?) {
        _plan.update {
            it.copy(
                testIntervalMinutes = minutes,
                isActive = if (minutes != null) true else it.isActive,
            ).withCurrentTimezone()
        }
        persistPlan()
        viewModelScope.launch { runCatching { savePlanToServer(_plan.value) } }
    }

    /**
     * Slots are wall-clock times, so the server needs to know which zone they belong to. Stamping
     * it on every save keeps it right if the user moves between zones.
     */
    private fun PublishingPlan.withCurrentTimezone(): PublishingPlan =
        copy(timezone = TimeZone.currentSystemDefault().id)

    fun clearFeedback() {
        _publishFeedback.value = null
    }

    /**
     * Epoch millis of the next test-mode publish attempt, or null when test mode isn't running or
     * the plan has never published yet (we don't fabricate an estimate in that case).
     */
    fun testModeNextAttemptMillis(): Long? {
        val plan = _plan.value
        val interval = plan.testIntervalMinutes
        if (interval == null || !plan.isActive) return null
        val last = plan.lastPublishedAtMillis ?: return null
        return last + interval * 60_000L
    }

    fun nextSlots(count: Int = 5): List<Long> {
        val plan = _plan.value
        if (!plan.isActive) return emptyList()
        val slots = effectiveSlots(plan)
        if (slots.isEmpty()) return emptyList()

        val now = Clock.System.now()
        val tz = TimeZone.currentSystemDefault()
        val today = now.toLocalDateTime(tz).date

        val result = mutableListOf<Long>()
        var checkDate = today
        var iterations = 0

        while (result.size < count && iterations < 100) {
            iterations++
            val dow = checkDate.dayOfWeek.isoDayNumber
            val slotsToday = slots.filter { it.dayOfWeek == dow }
            for (slot in slotsToday.sortedBy { it.hour * 60 + it.minute }) {
                val slotInstant = LocalDateTime(checkDate, LocalTime(slot.hour, slot.minute)).toInstant(tz)
                if (slotInstant > now) {
                    result.add(slotInstant.toEpochMilliseconds())
                    if (result.size >= count) break
                }
            }
            checkDate = checkDate.plus(1, DateTimeUnit.DAY)
        }
        return result
    }

    fun queueDurationWeeks(): Int {
        val slots = effectiveSlots(_plan.value)
        val slotsPerWeek = slots.size.coerceAtLeast(1)
        return _queue.value.size / slotsPerWeek
    }

    fun effectiveSlots(plan: PublishingPlan = _plan.value): List<WeeklySlot> =
        if (plan.mode == PlanMode.SLOTS) plan.weeklySlots
        else frequencyToSlots(plan.postsPerWeek, plan.preferredHour, plan.preferredMinute)

    private fun frequencyToSlots(perWeek: Int, hour: Int, minute: Int): List<WeeklySlot> {
        val days = when (perWeek.coerceIn(1, 7)) {
            1 -> listOf(3)
            2 -> listOf(2, 5)
            3 -> listOf(1, 3, 5)
            4 -> listOf(1, 2, 4, 5)
            5 -> listOf(1, 2, 3, 4, 5)
            6 -> listOf(1, 2, 3, 4, 5, 6)
            else -> listOf(1, 2, 3, 4, 5, 6, 7)
        }
        return days.map { WeeklySlot(it, hour, minute) }
    }

    private fun syncReorder() {
        viewModelScope.launch {
            val ids = _queue.value.map { it.id }
            runCatching { reorderServerQueue(ids) }
        }
    }

    private suspend fun syncWithServer() {
        val serverPlan = runCatching { loadPlanFromServer() }.getOrNull()
        if (serverPlan != null) {
            _plan.value = serverPlan
            persistPlan()
        }

        val serverQueue = runCatching { loadServerQueue() }.getOrNull()
        if (serverQueue != null) {
            val mapped = serverQueue.map { item ->
                QueuedPost(
                    id = item.id,
                    type = ContentType.entries.firstOrNull { it.name == item.type } ?: ContentType.VIDEO,
                    media = emptyList(),
                    title = item.title,
                    caption = item.caption,
                    platforms = item.platforms
                        .mapNotNull { name -> Platform.entries.firstOrNull { it.name == name } }
                        .toSet(),
                    youtubeAccountId = item.youtubeAccountId,
                    tiktokAccountId = item.tiktokAccountId,
                    addedAtMillis = 0L,
                    status = item.status,
                    errorMessage = item.errorMessage,
                )
            }
            _queue.value = mapped
            persistQueue()
        }
    }

    private fun loadQueue(): List<QueuedPost> {
        val raw = settings?.getStringOrNull(QUEUE_KEY) ?: return emptyList()
        return runCatching { json.decodeFromString<List<QueuedPost>>(raw) }.getOrDefault(emptyList())
    }

    private fun loadPlan(): PublishingPlan {
        val raw = settings?.getStringOrNull(PLAN_KEY) ?: return PublishingPlan()
        return runCatching { json.decodeFromString<PublishingPlan>(raw) }.getOrDefault(PublishingPlan())
    }

    private fun persistQueue() {
        settings?.putString(QUEUE_KEY, json.encodeToString(_queue.value))
    }

    private fun persistPlan() {
        settings?.putString(PLAN_KEY, json.encodeToString(_plan.value))
    }

    companion object {
        private const val QUEUE_KEY = "queued_posts_v1"
        private const val PLAN_KEY = "publishing_plan_v1"
    }
}

val DayOfWeek.isoDayNumber: Int get() = when (this) {
    DayOfWeek.MONDAY -> 1
    DayOfWeek.TUESDAY -> 2
    DayOfWeek.WEDNESDAY -> 3
    DayOfWeek.THURSDAY -> 4
    DayOfWeek.FRIDAY -> 5
    DayOfWeek.SATURDAY -> 6
    DayOfWeek.SUNDAY -> 7
    else -> 1
}

val Int.dayName: String get() = when (this) {
    1 -> "Pon"
    2 -> "Wt"
    3 -> "Śr"
    4 -> "Czw"
    5 -> "Pt"
    6 -> "Sob"
    7 -> "Niedz"
    else -> "?"
}

val Int.dayNameFull: String get() = when (this) {
    1 -> "Poniedziałek"
    2 -> "Wtorek"
    3 -> "Środa"
    4 -> "Czwartek"
    5 -> "Piątek"
    6 -> "Sobota"
    7 -> "Niedziela"
    else -> "?"
}
